import React, { useState } from 'react';
import { X, Plus, AlertCircle } from 'lucide-react';
import {
  OPERATION_TYPES,
  ENCAISSEMENT_TYPES,
  partnerAccountingService,
} from '../../../services/partnerAccountingService';

export default function PartnerOperationForm({ providers = [], onClose, onSuccess, initialData = null }) {
  const [form, setForm] = useState({
    providerId: initialData?.providerId || '',
    providerName: initialData?.providerName || '',
    clientName: initialData?.clientName || '',
    clientReference: initialData?.clientReference || '',
    shipmentReference: initialData?.shipmentReference || '',
    operationType: initialData?.operationType || 'enlevement_colis',
    operationDate: initialData?.operationDate || new Date()?.toISOString()?.split('T')?.[0],
    amountCollectedByPartner: initialData?.amountCollectedByPartner || '',
    partnerShare: initialData?.partnerShare || '',
    amountDueToAdt: initialData?.amountDueToAdt || '',
    encaissementType: initialData?.encaissementType || 'prestataire',
    notes: initialData?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleProviderChange = (e) => {
    const id = e?.target?.value;
    const provider = providers?.find(p => p?.id === id);
    setForm(f => ({
      ...f,
      providerId: id,
      providerName: provider?.name || f?.providerName,
    }));
  };

  const handleAmountChange = (field, value) => {
    const updated = { ...form, [field]: value };
    // Auto-calculate amountDueToAdt = collected - partnerShare
    const collected = Number(updated?.amountCollectedByPartner || 0);
    const share = Number(updated?.partnerShare || 0);
    if (field === 'amountCollectedByPartner' || field === 'partnerShare') {
      updated.amountDueToAdt = Math.max(0, collected - share)?.toFixed(2);
    }
    setForm(updated);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    if (!form?.providerName?.trim()) {
      setError('Le nom du prestataire est requis.');
      return;
    }
    if (!form?.amountCollectedByPartner || Number(form?.amountCollectedByPartner) <= 0) {
      setError('Le montant encaissé doit être supérieur à 0.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amountCollectedByPartner: Number(form?.amountCollectedByPartner),
        partnerShare: Number(form?.partnerShare || 0),
        amountDueToAdt: Number(form?.amountDueToAdt || 0),
        reversementStatus: 'a_reverser',
      };
      if (initialData?.id) {
        await partnerAccountingService?.updateOperation(initialData?.id, payload);
      } else {
        await partnerAccountingService?.createOperation(payload);
      }
      onSuccess?.();
    } catch (err) {
      setError(err?.message || 'Erreur lors de l\'enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const collected = Number(form?.amountCollectedByPartner || 0);
  const share = Number(form?.partnerShare || 0);
  const due = Number(form?.amountDueToAdt || 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {initialData ? 'Modifier l\'opération' : 'Nouvelle opération partenaire'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Enregistrer un encaissement effectué par un prestataire
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Provider */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prestataire *
              </label>
              {providers?.length > 0 ? (
                <select
                  value={form?.providerId}
                  onChange={handleProviderChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">— Sélectionner un prestataire —</option>
                  {providers?.map(p => (
                    <option key={p?.id} value={p?.id}>{p?.name}</option>
                  ))}
                  <option value="__manual__">Saisir manuellement</option>
                </select>
              ) : null}
              {(form?.providerId === '__manual__' || providers?.length === 0) && (
                <input
                  type="text"
                  value={form?.providerName}
                  onChange={e => setForm(f => ({ ...f, providerName: e?.target?.value }))}
                  placeholder="Nom du prestataire"
                  className="mt-2 w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Client concerné
              </label>
              <input
                type="text"
                value={form?.clientName}
                onChange={e => setForm(f => ({ ...f, clientName: e?.target?.value }))}
                placeholder="Nom du client"
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Type & Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type d'opération *
              </label>
              <select
                value={form?.operationType}
                onChange={e => setForm(f => ({ ...f, operationType: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
              >
                {OPERATION_TYPES?.map(t => (
                  <option key={t?.value} value={t?.value}>{t?.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date de l'opération *
              </label>
              <input
                type="date"
                value={form?.operationDate}
                onChange={e => setForm(f => ({ ...f, operationDate: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* References */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Référence commande / envoi
              </label>
              <input
                type="text"
                value={form?.clientReference}
                onChange={e => setForm(f => ({ ...f, clientReference: e?.target?.value }))}
                placeholder="Ex: PROMO-ETE-001"
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Qui encaisse ? *
              </label>
              <select
                value={form?.encaissementType}
                onChange={e => setForm(f => ({ ...f, encaissementType: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
              >
                {ENCAISSEMENT_TYPES?.map(t => (
                  <option key={t?.value} value={t?.value}>{t?.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Amounts */}
          <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl p-4 space-y-4">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Répartition financière
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Montant payé par le client (€) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form?.amountCollectedByPartner}
                  onChange={e => handleAmountChange('amountCollectedByPartner', e?.target?.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Part prestataire (€)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form?.partnerShare}
                  onChange={e => handleAmountChange('partnerShare', e?.target?.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Montant dû à ADT (€)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form?.amountDueToAdt}
                  onChange={e => handleAmountChange('amountDueToAdt', e?.target?.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            {/* Recap */}
            {collected > 0 && (
              <div className="grid grid-cols-3 gap-3 pt-2 border-t border-gray-200 dark:border-slate-700">
                <div className="text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Encaissé</p>
                  <p className="font-bold text-gray-900 dark:text-white">{collected?.toFixed(2)}€</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-orange-500">Part prestataire</p>
                  <p className="font-bold text-orange-600">{share?.toFixed(2)}€</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-blue-500">Dû à ADT</p>
                  <p className="font-bold text-blue-600">{due?.toFixed(2)}€</p>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes internes
            </label>
            <textarea
              value={form?.notes}
              onChange={e => setForm(f => ({ ...f, notes: e?.target?.value }))}
              rows={3}
              placeholder="Informations complémentaires..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {saving ? (
                <><span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Enregistrement...</>
              ) : (
                <><Plus className="h-4 w-4" />{initialData ? 'Mettre à jour' : 'Enregistrer l\'opération'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
